/**
 * Magnetic declination — "variación" on a chart — where the aircraft is.
 *
 * ## Why the app computes it rather than asking for one number
 *
 * Until 2026-10-03 the whole app ran on a single setting, −8°, and it was used
 * for every conversion: a METAR wind at Ushuaia, a runway at Mendoza, a track
 * from Buenos Aires to Bariloche. Declination across Argentina spans about
 * twenty degrees — roughly 9° W over the Río de la Plata, a few degrees W in
 * Cuyo, and several degrees **E** in Tierra del Fuego — so one number is right
 * for one region and quietly wrong everywhere else. A magnetic track out by six
 * degrees is a heading a pilot will fly.
 *
 * So the World Magnetic Model is evaluated at the place in question. It is the
 * model behind every GPS navigator's magnetic readout and the one the charts are
 * drawn against, it is public domain, and it is ninety coefficients — small
 * enough to ship and evaluate on a phone with no signal.
 *
 * ## Sign convention
 *
 * East positive, west negative, the same as `magneticFromTrue` in
 * `core/navigation/wind.ts`: magnetic = true − declination. `9° W` is `-9`.
 *
 * ## What this is not
 *
 * The WMM models the **main field**. A local anomaly, a crustal feature, a
 * magnetic storm or the aircraft's own deviation is not in it, and NOAA states
 * its typical uncertainty as about half a degree at mid latitudes. It is the
 * right figure for planning and for converting a published true value; the
 * aircraft's compass and the published magnetic values on the chart remain the
 * reference (rule 5).
 *
 * Pure: the caller supplies the date.
 */

import { WMM2025_COEFFICIENTS, WMM2025_EPOCH, WMM2025_VALID_UNTIL } from './wmm2025'

/** WGS-84, as the WMM report specifies. */
const SEMI_MAJOR_KM = 6378.137
const FLATTENING = 1 / 298.257223563
const ECCENTRICITY_SQUARED = FLATTENING * (2 - FLATTENING)
/** The geomagnetic reference radius the coefficients are expressed against. */
const REFERENCE_RADIUS_KM = 6371.2
const MAX_DEGREE = 12

const DEG = Math.PI / 180

/** Coefficient tables indexed `[n][m]`, built once. */
const G: number[][] = []
const H: number[][] = []
const G_DOT: number[][] = []
const H_DOT: number[][] = []
for (let n = 0; n <= MAX_DEGREE; n += 1) {
	G.push(new Array(n + 1).fill(0))
	H.push(new Array(n + 1).fill(0))
	G_DOT.push(new Array(n + 1).fill(0))
	H_DOT.push(new Array(n + 1).fill(0))
}
for (const [n, m, g, h, gDot, hDot] of WMM2025_COEFFICIENTS) {
	G[n]![m] = g
	H[n]![m] = h
	G_DOT[n]![m] = gDot
	H_DOT[n]![m] = hDot
}

/**
 * Schmidt semi-normalisation factors, `S[n][m]`.
 *
 * The recursion below produces Gauss-normalised Legendre functions; the
 * coefficients are Schmidt semi-normalised, and this is the factor between
 * the two. Computed once rather than per call.
 */
const SCHMIDT: number[][] = []
for (let n = 0; n <= MAX_DEGREE; n += 1) {
	SCHMIDT.push(new Array(n + 1).fill(0))
	SCHMIDT[n]![0] = n === 0 ? 1 : (SCHMIDT[n - 1]![0]! * (2 * n - 1)) / n
	for (let m = 1; m <= n; m += 1) {
		const kronecker = m === 1 ? 2 : 1
		SCHMIDT[n]![m] = SCHMIDT[n]![m - 1]! * Math.sqrt(((n - m + 1) * kronecker) / (n + m))
	}
}

export interface MagneticField {
	/** Declination, degrees, east positive. */
	declinationDeg: number
	/** Inclination ("dip"), degrees, down positive. */
	inclinationDeg: number
	/** Horizontal intensity, nT. */
	horizontalNt: number
	/** North, east and down components, nT, in the geodetic frame. */
	northNt: number
	eastNt: number
	downNt: number
	/** Whether the date lies inside the model's five-year validity. */
	withinValidity: boolean
}

/**
 * The decimal year of a date, the way the model takes it: 2026-07-02 ≈ 2026.5.
 */
export function decimalYear(date: Date): number {
	const year = date.getUTCFullYear()
	const start = Date.UTC(year, 0, 1)
	const end = Date.UTC(year + 1, 0, 1)
	return year + (date.getTime() - start) / (end - start)
}

/**
 * Evaluate the main field at a geodetic position.
 *
 * `altitudeKm` is height above the WGS-84 ellipsoid. For aviation the
 * difference between the field at the surface and at FL100 is a few hundredths
 * of a degree of declination, so callers planning a flight may pass zero.
 *
 * Returns null for a position that is not one, rather than a number.
 */
export function magneticField(
	latDeg: number,
	lonDeg: number,
	year: number,
	altitudeKm = 0,
): MagneticField | null {
	if (![latDeg, lonDeg, year, altitudeKm].every(Number.isFinite)) return null
	if (Math.abs(latDeg) > 90) return null

	const dt = year - WMM2025_EPOCH
	const phi = latDeg * DEG
	const lambda = lonDeg * DEG

	// Geodetic → geocentric spherical (WMM technical report, eq. 7–8).
	const sinPhi = Math.sin(phi)
	const cosPhi = Math.cos(phi)
	const curvatureRadius = SEMI_MAJOR_KM / Math.sqrt(1 - ECCENTRICITY_SQUARED * sinPhi * sinPhi)
	const p = (curvatureRadius + altitudeKm) * cosPhi
	const z = (curvatureRadius * (1 - ECCENTRICITY_SQUARED) + altitudeKm) * sinPhi
	const r = Math.sqrt(p * p + z * z)
	const phiPrime = Math.asin(z / r)

	// Colatitude, for the Legendre recursion.
	const cosTheta = Math.sin(phiPrime)
	const sinTheta = Math.cos(phiPrime)

	// Gauss-normalised associated Legendre functions and their θ-derivatives.
	const P: number[][] = []
	const dP: number[][] = []
	for (let n = 0; n <= MAX_DEGREE; n += 1) {
		P.push(new Array(n + 1).fill(0))
		dP.push(new Array(n + 1).fill(0))
	}
	P[0]![0] = 1
	for (let n = 1; n <= MAX_DEGREE; n += 1) {
		for (let m = 0; m <= n; m += 1) {
			if (n === m) {
				P[n]![m] = sinTheta * P[n - 1]![m - 1]!
				dP[n]![m] = sinTheta * dP[n - 1]![m - 1]! + cosTheta * P[n - 1]![m - 1]!
			} else if (n === 1) {
				P[n]![m] = cosTheta * P[0]![0]!
				dP[n]![m] = cosTheta * dP[0]![0]! - sinTheta * P[0]![0]!
			} else {
				const k = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3))
				const twoBack = m <= n - 2 ? P[n - 2]![m]! : 0
				const twoBackD = m <= n - 2 ? dP[n - 2]![m]! : 0
				P[n]![m] = cosTheta * P[n - 1]![m]! - k * twoBack
				dP[n]![m] = cosTheta * dP[n - 1]![m]! - sinTheta * P[n - 1]![m]! - k * twoBackD
			}
		}
	}

	let radial = 0
	let colatitude = 0
	let longitude = 0
	const ratio = REFERENCE_RADIUS_KM / r
	let power = ratio * ratio // (a/r)^(n+2) begins at n = 0 as (a/r)^2
	for (let n = 1; n <= MAX_DEGREE; n += 1) {
		power *= ratio
		for (let m = 0; m <= n; m += 1) {
			const g = G[n]![m]! + dt * G_DOT[n]![m]!
			const h = H[n]![m]! + dt * H_DOT[n]![m]!
			const cosM = Math.cos(m * lambda)
			const sinM = Math.sin(m * lambda)
			const schmidtP = SCHMIDT[n]![m]! * P[n]![m]!
			const schmidtDP = SCHMIDT[n]![m]! * dP[n]![m]!
			const term = g * cosM + h * sinM
			radial += (n + 1) * power * term * schmidtP
			colatitude -= power * term * schmidtDP
			longitude -= power * m * (-g * sinM + h * cosM) * schmidtP
		}
	}
	// At a geographic pole the east component is indeterminate; the model's own
	// software special-cases it, and nobody plans a light-aircraft leg there.
	const eastPrime = sinTheta === 0 ? 0 : longitude / sinTheta
	const northPrime = -colatitude
	const downPrime = -radial

	// Back to the geodetic frame (eq. 17).
	const psi = phiPrime - phi
	const north = northPrime * Math.cos(psi) - downPrime * Math.sin(psi)
	const east = eastPrime
	const down = northPrime * Math.sin(psi) + downPrime * Math.cos(psi)
	const horizontal = Math.hypot(north, east)

	return {
		declinationDeg: Math.atan2(east, north) / DEG,
		inclinationDeg: Math.atan2(down, horizontal) / DEG,
		horizontalNt: horizontal,
		northNt: north,
		eastNt: east,
		downNt: down,
		withinValidity: year >= WMM2025_EPOCH && year < WMM2025_VALID_UNTIL,
	}
}

/**
 * Declination at a place on a date, degrees east positive, or null.
 *
 * Null outside the model's validity as well as for a bad position: a model
 * extrapolated past its five years drifts by a few tenths of a degree a year,
 * and the caller has a pilot-entered value to fall back to that it can name.
 */
export function declinationAt(position: { lat: number; lon: number }, date: Date): number | null {
	const field = magneticField(position.lat, position.lon, decimalYear(date))
	if (!field || !field.withinValidity) return null
	return field.declinationDeg
}

/**
 * `9° W`, `3° E`, `0°` — how a chart writes it. Whole degrees: the model is
 * good to about half a degree, and a tenth printed beside a heading invites
 * reading a precision that is not there.
 */
export function formatDeclination(declinationDeg: number): string {
	const rounded = Math.round(declinationDeg)
	if (rounded === 0) return '0°'
	return `${Math.abs(rounded)}° ${rounded < 0 ? 'W' : 'E'}`
}

export { WMM2025_EPOCH, WMM2025_VALID_UNTIL }
